import type {
  RepoState,
  Commit,
  LogLine,
  RemoteState,
  ConflictState,
  ConflictHunk,
  HunkResolution,
  ReflogEntry,
} from "./types";

export function deepClone<T>(o: T): T {
  return JSON.parse(JSON.stringify(o));
}

export function randomSha(): string {
  const hex = "0123456789abcdef";
  let s = "";
  for (let i = 0; i < 7; i++) s += hex[Math.floor(Math.random() * 16)];
  return s;
}

export function promptCommitMessage(branch: string): string {
  if (branch === "feature") return "Refresh homepage copy";
  return "Update homepage heading";
}

export function isAncestorOf(
  ancestorId: string,
  descendantId: string,
  commits: Commit[],
): boolean {
  let cur: Commit | undefined = commits.find((c) => c.id === descendantId);
  while (cur) {
    if (cur.id === ancestorId) return true;
    if (!cur.parent) return false;
    cur = commits.find((c) => c.id === cur!.parent);
  }
  return false;
}

export interface OpResult {
  repo: RepoState;
  log: LogLine[];
}

export function editFile(
  repo: RepoState,
  name: string,
  content: string,
): RepoState {
  const f = repo.files[name];
  const isClean = content === f.committed;
  const stillStaged = f.status === "staged" && content === f.staged;
  return {
    ...repo,
    files: {
      ...repo.files,
      [name]: {
        ...f,
        content,
        status: stillStaged ? "staged" : isClean ? "clean" : "modified",
      },
    },
  };
}

export function stageAll(repo: RepoState): OpResult & { stagedNames: string[] } {
  const next = { ...repo.files };
  const stagedNames: string[] = [];
  Object.entries(repo.files).forEach(([name, f]) => {
    if (f.status === "modified") {
      next[name] = { ...f, status: "staged", staged: f.content };
      stagedNames.push(name);
    }
  });
  if (stagedNames.length === 0) return { repo, log: [], stagedNames };
  const log: LogLine[] = [
    { kind: "cmd", text: `git add ${stagedNames.join(" ")}` },
    {
      kind: "out",
      text: `Staged ${stagedNames.length} file${stagedNames.length === 1 ? "" : "s"}: ${stagedNames.join(", ")}`,
    },
  ];
  return { repo: { ...repo, files: next }, log, stagedNames };
}

export function makeCommit(
  repo: RepoState,
  msgOverride?: string,
): OpResult & { committed: boolean } {
  if (repo.conflict) {
    return {
      repo,
      committed: false,
      log: [
        {
          kind: "err",
          text: "error: Committing is not possible because you have unmerged files. Resolve the conflicts first.",
        },
      ],
    };
  }
  const stagedNow = Object.entries(repo.files).filter(
    ([, f]) => f.status === "staged",
  );
  if (stagedNow.length === 0) {
    return {
      repo,
      committed: false,
      log: [
        {
          kind: "err",
          text: "nothing to commit, working tree clean (no staged changes)",
        },
      ],
    };
  }
  const branch = repo.head;
  const parent = repo.branches[branch];
  const parentCommit = repo.commits.find((c) => c.id === parent);
  const newId = randomSha();
  const isMergeCommit = !!repo.pendingMerge;
  const msg =
    msgOverride ||
    (isMergeCommit
      ? `Merge branch '${repo.pendingMerge!.sourceBranch}' into ${branch}`
      : promptCommitMessage(branch));
  const sourceTipCommit = isMergeCommit
    ? repo.commits.find((c) => c.id === repo.pendingMerge!.sourceTip)
    : undefined;
  const newCommit: Commit = {
    id: newId,
    msg,
    branch,
    parent,
    parent2: isMergeCommit ? repo.pendingMerge!.sourceTip : undefined,
    // Merge commit sits one column past the later of the two parents.
    x:
      (Math.max(
        parentCommit?.x ?? 0,
        sourceTipCommit?.x ?? 0,
      )) + 1,
  };

  const nextFiles = { ...repo.files };
  stagedNow.forEach(([name, f]) => {
    nextFiles[name] = {
      ...f,
      committed: f.staged!,
      content: f.staged!,
      staged: null,
      status: "clean",
    };
  });

  const log: LogLine[] = isMergeCommit
    ? [
        { kind: "cmd", text: "git commit" },
        { kind: "ok", text: `[${branch} ${newId.slice(0, 7)}] ${msg}` },
        {
          kind: "out",
          text: ` Merge made by the 'ort' strategy.`,
        },
      ]
    : [
        { kind: "cmd", text: `git commit -m "${msg}"` },
        { kind: "ok", text: `[${branch} ${newId.slice(0, 7)}] ${msg}` },
        {
          kind: "out",
          text: ` ${stagedNow.length} file${stagedNow.length === 1 ? "" : "s"} changed`,
        },
      ];

  const nextReflog: ReflogEntry[] = [
    ...(repo.reflog ?? []),
    { ref: "HEAD", commitId: newId, action: `commit: ${msg}` },
  ];

  return {
    repo: {
      ...repo,
      commits: [...repo.commits, newCommit],
      branches: { ...repo.branches, [branch]: newId },
      files: nextFiles,
      pendingMerge: null,
      reflog: nextReflog,
    },
    committed: true,
    log,
  };
}

// ----------------- CONFLICT MERGE -----------------

/**
 * Lesson-supplied scenario for a guaranteed conflict. Avoids implementing real
 * three-way diff — the merge-conflicts course pre-stages the divergence.
 */
export interface ConflictScenario {
  file: string;
  /**
   * Segments that make up the file. Literal segments are unchanged context;
   * hunk segments expand to either conflict markers (when unresolved) or the
   * chosen resolution (once resolved).
   */
  hunks: ConflictHunk[];
  /** Alternating literal blocks; one more entry than hunks. */
  literals: string[];
}

/** Render a conflict file in its current state (with markers or resolutions). */
export function renderConflictFile(conflict: ConflictState): string {
  // Reconstruct literal-hunk-literal-hunk-... from segments.
  let out = "";
  for (const seg of conflict.segments) {
    if (seg.kind === "literal") {
      out += seg.text;
      continue;
    }
    const h = conflict.hunks[seg.hunkIndex];
    if (!h.resolution) {
      out += `<<<<<<< ${h.oursLabel}\n${h.ours}=======\n${h.theirs}>>>>>>> ${h.theirsLabel}\n`;
    } else if (h.resolution === "ours") {
      out += h.ours;
    } else if (h.resolution === "theirs") {
      out += h.theirs;
    } else if (h.resolution === "both") {
      out += h.ours + h.theirs;
    } else {
      out += h.customText ?? "";
    }
  }
  return out;
}

export function startConflictMerge(
  repo: RepoState,
  source: string,
  scenario: ConflictScenario,
): OpResult & { conflicted: boolean } {
  const target = repo.head;
  const targetTip = repo.branches[target];
  const sourceTip = repo.branches[source];
  if (!sourceTip) {
    return {
      repo,
      conflicted: false,
      log: [{ kind: "err", text: `merge: ${source} - not something we can merge` }],
    };
  }
  if (targetTip === sourceTip) {
    return {
      repo,
      conflicted: false,
      log: [{ kind: "out", text: "Already up to date." }],
    };
  }

  const segments: ConflictState["segments"] = [];
  for (let i = 0; i < scenario.literals.length; i++) {
    segments.push({ kind: "literal", text: scenario.literals[i] });
    if (i < scenario.hunks.length) {
      segments.push({ kind: "hunk", hunkIndex: i });
    }
  }
  const conflict: ConflictState = {
    file: scenario.file,
    targetBranch: target,
    sourceBranch: source,
    sourceTip,
    hunks: scenario.hunks.map((h) => ({ ...h, resolution: null })),
    segments,
  };

  const marked = renderConflictFile(conflict);
  const file = repo.files[scenario.file];
  const nextFiles = {
    ...repo.files,
    [scenario.file]: {
      ...file,
      content: marked,
      staged: null,
      status: "conflicted" as const,
    },
  };

  return {
    repo: {
      ...repo,
      files: nextFiles,
      conflict,
    },
    conflicted: true,
    log: [
      { kind: "cmd", text: `git merge ${source}` },
      { kind: "out", text: `Auto-merging ${scenario.file}` },
      {
        kind: "err",
        text: `CONFLICT (content): Merge conflict in ${scenario.file}`,
      },
      {
        kind: "info",
        text: "Automatic merge failed; fix conflicts and then commit the result.",
      },
    ],
  };
}

export function resolveHunk(
  repo: RepoState,
  hunkIndex: number,
  resolution: HunkResolution,
  customText?: string,
): RepoState {
  if (!repo.conflict) return repo;
  const hunks = repo.conflict.hunks.map((h, i) =>
    i === hunkIndex
      ? { ...h, resolution, customText: resolution === "custom" ? customText ?? "" : undefined }
      : h,
  );
  const conflict: ConflictState = { ...repo.conflict, hunks };
  const file = repo.files[conflict.file];
  const content = renderConflictFile(conflict);
  const allResolved = hunks.every((h) => h.resolution !== null);
  return {
    ...repo,
    conflict,
    files: {
      ...repo.files,
      [conflict.file]: {
        ...file,
        content,
        // Still conflicted in working tree until learner clicks "mark resolved".
        status: allResolved ? file.status : "conflicted",
      },
    },
  };
}

/**
 * Equivalent to `git add <file>` after a manual conflict edit — flips the file
 * from "conflicted" to "staged", clears repo.conflict, sets pendingMerge so the
 * next commit becomes a merge commit.
 */
export function finalizeConflict(repo: RepoState): OpResult & { finalized: boolean } {
  const conflict = repo.conflict;
  if (!conflict) {
    return { repo, finalized: false, log: [] };
  }
  if (conflict.hunks.some((h) => h.resolution === null)) {
    return {
      repo,
      finalized: false,
      log: [
        {
          kind: "err",
          text: `error: ${conflict.file} still has unresolved conflict markers`,
        },
      ],
    };
  }
  const file = repo.files[conflict.file];
  const resolved = renderConflictFile(conflict);
  return {
    repo: {
      ...repo,
      conflict: null,
      pendingMerge: {
        sourceBranch: conflict.sourceBranch,
        sourceTip: conflict.sourceTip,
      },
      files: {
        ...repo.files,
        [conflict.file]: {
          ...file,
          content: resolved,
          staged: resolved,
          status: "staged",
        },
      },
    },
    finalized: true,
    log: [
      { kind: "cmd", text: `git add ${conflict.file}` },
      {
        kind: "ok",
        text: `Marked ${conflict.file} as resolved. Run 'git commit' to finish the merge.`,
      },
    ],
  };
}

/** Abort an in-progress merge — restore the file from HEAD, clear conflict. */
export function abortMerge(repo: RepoState): OpResult {
  if (!repo.conflict && !repo.pendingMerge) {
    return { repo, log: [{ kind: "out", text: "No merge in progress." }] };
  }
  const conflict = repo.conflict;
  const nextFiles = { ...repo.files };
  if (conflict) {
    const file = nextFiles[conflict.file];
    nextFiles[conflict.file] = {
      ...file,
      content: file.committed,
      staged: null,
      status: "clean",
    };
  }
  return {
    repo: {
      ...repo,
      files: nextFiles,
      conflict: null,
      pendingMerge: null,
    },
    log: [
      { kind: "cmd", text: "git merge --abort" },
      { kind: "ok", text: "Merge aborted. Working tree restored." },
    ],
  };
}

export function createAndSwitchToFeature(repo: RepoState): OpResult {
  if (repo.branches.feature) {
    return switchBranch(repo, "feature");
  }
  const tipId = repo.branches[repo.head];
  return {
    repo: {
      ...repo,
      branches: { ...repo.branches, feature: tipId },
      head: "feature",
    },
    log: [
      { kind: "cmd", text: "git switch -c feature" },
      { kind: "ok", text: "Switched to a new branch 'feature'" },
    ],
  };
}

export function switchBranch(repo: RepoState, name: string): OpResult {
  if (!repo.branches[name]) {
    return {
      repo,
      log: [
        {
          kind: "err",
          text: `error: pathspec '${name}' did not match any known refs`,
        },
      ],
    };
  }
  if (repo.head === name) return { repo, log: [] };
  const nextFiles = { ...repo.files };
  Object.entries(repo.files).forEach(([fname, f]) => {
    nextFiles[fname] = {
      ...f,
      content: f.committed,
      staged: null,
      status: "clean",
    };
  });
  return {
    repo: { ...repo, head: name, files: nextFiles },
    log: [
      { kind: "cmd", text: `git switch ${name}` },
      { kind: "ok", text: `Switched to branch '${name}'` },
    ],
  };
}

// ----------------- REMOTE OPS -----------------

function getOrigin(repo: RepoState): RemoteState | undefined {
  return repo.remotes?.origin;
}

/** Commits in branch's local history that are not yet on origin/<branch>. */
function commitsAheadOfOrigin(
  repo: RepoState,
  branch: string,
): Commit[] {
  const origin = getOrigin(repo);
  const localTip = repo.branches[branch];
  if (!localTip) return [];
  const remoteTip = origin?.branches[branch];
  const out: Commit[] = [];
  let cur: Commit | undefined = repo.commits.find((c) => c.id === localTip);
  while (cur) {
    if (cur.id === remoteTip) break;
    out.unshift(cur);
    if (!cur.parent) break;
    cur = repo.commits.find((c) => c.id === cur!.parent);
  }
  return out;
}

/** Commits on origin/<branch> not yet on local <branch>. */
function commitsBehindOrigin(
  repo: RepoState,
  branch: string,
): Commit[] {
  const origin = getOrigin(repo);
  if (!origin) return [];
  const remoteTip = origin.branches[branch];
  const localTip = repo.branches[branch];
  if (!remoteTip) return [];
  // Every commit the local branch already has, walking back from its tip. We
  // stop counting "behind" as soon as origin's history reaches one of these —
  // i.e. the shared ancestor. Comparing against this whole set (not just the
  // local tip) means a local branch that has diverged with its own commits is
  // correctly reported as 0 behind, instead of falsely "behind" by everything
  // on origin because the local tip happens not to appear in origin's chain.
  const localReachable = new Set<string>();
  let l: Commit | undefined = repo.commits.find((c) => c.id === localTip);
  while (l) {
    localReachable.add(l.id);
    if (!l.parent) break;
    l = repo.commits.find((c) => c.id === l!.parent);
  }
  const out: Commit[] = [];
  let cur: Commit | undefined = origin.commits.find((c) => c.id === remoteTip);
  while (cur) {
    if (localReachable.has(cur.id)) break;
    out.unshift(cur);
    if (!cur.parent) break;
    cur = origin.commits.find((c) => c.id === cur!.parent);
  }
  return out;
}

export function aheadBehind(
  repo: RepoState,
  branch: string,
): { ahead: number; behind: number } {
  return {
    ahead: commitsAheadOfOrigin(repo, branch).length,
    behind: commitsBehindOrigin(repo, branch).length,
  };
}

export function cloneFromOrigin(repo: RepoState): OpResult & { cloned: boolean } {
  const origin = getOrigin(repo);
  if (!origin) {
    return {
      repo,
      cloned: false,
      log: [{ kind: "err", text: "fatal: no origin to clone from" }],
    };
  }
  if (repo.commits.length > 0) {
    return {
      repo,
      cloned: false,
      log: [
        {
          kind: "err",
          text: "destination already exists and is not an empty directory",
        },
      ],
    };
  }
  const defaultBranch = "main";
  const tipId = origin.branches[defaultBranch];
  const nextFiles: RepoState["files"] = {};
  for (const [name, content] of Object.entries(origin.files)) {
    nextFiles[name] = {
      content,
      committed: content,
      staged: null,
      status: "clean",
    };
  }
  return {
    repo: {
      ...repo,
      commits: origin.commits.map((c) => ({ ...c })),
      branches: { [defaultBranch]: tipId },
      head: defaultBranch,
      files: nextFiles,
      tracking: {
        ...(repo.tracking ?? {}),
        [defaultBranch]: { remote: "origin", branch: defaultBranch },
      },
    },
    cloned: true,
    log: [
      { kind: "cmd", text: "git clone https://github.com/nymbl/atlas.git" },
      { kind: "out", text: "Cloning into 'atlas'..." },
      {
        kind: "out",
        text: `remote: Enumerating objects: ${origin.commits.length * 3}, done.`,
      },
      { kind: "out", text: `remote: Total ${origin.commits.length} commits` },
      { kind: "ok", text: "Receiving objects: 100%, done." },
    ],
  };
}

export function pushBranch(
  repo: RepoState,
  branch: string,
): OpResult & { pushed: boolean } {
  const origin = getOrigin(repo);
  if (!origin) {
    return {
      repo,
      pushed: false,
      log: [{ kind: "err", text: "fatal: no upstream configured (origin)" }],
    };
  }
  const ahead = commitsAheadOfOrigin(repo, branch);
  const behind = commitsBehindOrigin(repo, branch);
  if (behind.length > 0) {
    return {
      repo,
      pushed: false,
      log: [
        { kind: "cmd", text: `git push origin ${branch}` },
        {
          kind: "err",
          text:
            "! [rejected]        main -> main (non-fast-forward)\nhint: Updates were rejected because the remote contains work that you do not have locally. Run 'git pull' first.",
        },
      ],
    };
  }
  if (ahead.length === 0) {
    return {
      repo,
      pushed: false,
      log: [
        { kind: "cmd", text: `git push origin ${branch}` },
        { kind: "out", text: "Everything up-to-date" },
      ],
    };
  }
  const localTip = repo.branches[branch];
  const newOriginCommits = [...origin.commits];
  for (const c of ahead) {
    if (!newOriginCommits.find((oc) => oc.id === c.id)) {
      newOriginCommits.push({ ...c });
    }
  }
  return {
    repo: {
      ...repo,
      remotes: {
        ...repo.remotes,
        origin: {
          ...origin,
          commits: newOriginCommits,
          branches: { ...origin.branches, [branch]: localTip },
        },
      },
      tracking: {
        ...(repo.tracking ?? {}),
        [branch]: { remote: "origin", branch },
      },
    },
    pushed: true,
    log: [
      { kind: "cmd", text: `git push origin ${branch}` },
      { kind: "out", text: "Enumerating objects: " + ahead.length * 3 + ", done." },
      { kind: "out", text: "Writing objects: 100% done." },
      {
        kind: "ok",
        text: `To origin/${branch}\n   ${ahead[0].parent?.slice(0, 7) ?? "0000000"}..${localTip.slice(0, 7)}  ${branch} -> ${branch}`,
      },
    ],
  };
}

export function fetchOrigin(
  repo: RepoState,
  branch: string,
): OpResult & { fetched: boolean } {
  const origin = getOrigin(repo);
  if (!origin) {
    return {
      repo,
      fetched: false,
      log: [{ kind: "err", text: "fatal: 'origin' does not appear to be a git repository" }],
    };
  }
  const behind = commitsBehindOrigin(repo, branch);
  if (behind.length === 0) {
    return {
      repo,
      fetched: false,
      log: [
        { kind: "cmd", text: "git fetch origin" },
        { kind: "out", text: "Already up to date." },
      ],
    };
  }
  // Copy missing origin commits into local commit pool so the graph can render them.
  const nextCommits = [...repo.commits];
  for (const c of behind) {
    if (!nextCommits.find((lc) => lc.id === c.id)) {
      nextCommits.push({ ...c });
    }
  }
  const remoteTip = origin.branches[branch]!;
  return {
    repo: {
      ...repo,
      commits: nextCommits,
    },
    fetched: true,
    log: [
      { kind: "cmd", text: "git fetch origin" },
      { kind: "out", text: "remote: Enumerating objects: " + behind.length * 2 },
      {
        kind: "info",
        text: `   ${behind[0].parent?.slice(0, 7) ?? "0000000"}..${remoteTip.slice(0, 7)}  ${branch}     -> origin/${branch}`,
      },
    ],
  };
}

export function pullOrigin(
  repo: RepoState,
  branch: string,
): OpResult & { pulled: boolean } {
  const origin = getOrigin(repo);
  if (!origin) {
    return {
      repo,
      pulled: false,
      log: [{ kind: "err", text: "fatal: no remote 'origin' configured" }],
    };
  }
  const ahead = commitsAheadOfOrigin(repo, branch);
  const behind = commitsBehindOrigin(repo, branch);
  if (behind.length === 0) {
    return {
      repo,
      pulled: false,
      log: [
        { kind: "cmd", text: `git pull origin ${branch}` },
        { kind: "out", text: "Already up to date." },
      ],
    };
  }
  if (ahead.length > 0) {
    return {
      repo,
      pulled: false,
      log: [
        { kind: "cmd", text: `git pull origin ${branch}` },
        {
          kind: "err",
          text: "Non-fast-forward pull needed (local has diverging commits) — covered in the merge-conflicts course.",
        },
      ],
    };
  }
  // Fast-forward: bring origin commits in and move local tip forward.
  const nextCommits = [...repo.commits];
  for (const c of behind) {
    if (!nextCommits.find((lc) => lc.id === c.id)) {
      nextCommits.push({ ...c });
    }
  }
  const remoteTip = origin.branches[branch]!;
  const localTip = repo.branches[branch];
  // Update file contents to match the tip if the remote tracked a file change.
  // Files snapshot on the remote represents the state at the tip.
  const nextFiles = { ...repo.files };
  for (const [name, content] of Object.entries(origin.files)) {
    const existing = nextFiles[name];
    if (existing) {
      // Only fast-forward files that aren't locally dirty.
      if (
        existing.status === "clean" &&
        existing.content === existing.committed
      ) {
        nextFiles[name] = {
          content,
          committed: content,
          staged: null,
          status: "clean",
        };
      } else {
        nextFiles[name] = { ...existing, committed: content };
      }
    } else {
      nextFiles[name] = {
        content,
        committed: content,
        staged: null,
        status: "clean",
      };
    }
  }
  return {
    repo: {
      ...repo,
      commits: nextCommits,
      branches: { ...repo.branches, [branch]: remoteTip },
      files: nextFiles,
      tracking: {
        ...(repo.tracking ?? {}),
        [branch]: { remote: "origin", branch },
      },
    },
    pulled: true,
    log: [
      { kind: "cmd", text: `git pull origin ${branch}` },
      { kind: "out", text: `From origin` },
      {
        kind: "info",
        text: `Updating ${localTip?.slice(0, 7)}..${remoteTip.slice(0, 7)}`,
      },
      {
        kind: "ok",
        text: `Fast-forward — ${branch} now points at ${remoteTip.slice(0, 7)}`,
      },
    ],
  };
}

export function mergeInto(
  repo: RepoState,
  target: string,
  source: string,
): OpResult & { merged: boolean } {
  if (repo.head !== target) {
    return {
      repo,
      merged: false,
      log: [
        {
          kind: "err",
          text: `You must be on ${target} to merge ${source}. Run 'git switch ${target}' first.`,
        },
      ],
    };
  }
  const targetTip = repo.branches[target];
  const sourceTip = repo.branches[source];
  if (!sourceTip || targetTip === sourceTip) {
    return {
      repo,
      merged: false,
      log: [{ kind: "out", text: "Already up to date." }],
    };
  }
  const isAnc = isAncestorOf(targetTip, sourceTip, repo.commits);
  if (!isAnc) {
    return {
      repo,
      merged: false,
      log: [
        {
          kind: "err",
          text: "Non-fast-forward merge needed — covered in the next lesson.",
        },
      ],
    };
  }
  return {
    repo: {
      ...repo,
      branches: { ...repo.branches, [target]: sourceTip },
    },
    merged: true,
    log: [
      { kind: "cmd", text: `git merge ${source}` },
      {
        kind: "info",
        text: `Updating ${targetTip.slice(0, 7)}..${sourceTip.slice(0, 7)}`,
      },
      {
        kind: "ok",
        text: `Fast-forward — ${target} now points at ${sourceTip.slice(0, 7)}`,
      },
    ],
  };
}

// ----------------- UNDO / RECOVERY OPS -----------------

function pushReflog(
  repo: RepoState,
  entry: ReflogEntry,
): ReflogEntry[] {
  return [...(repo.reflog ?? []), entry];
}

/** Recompute which commits are unreachable from any branch tip. */
function recomputeOrphans(
  commits: Commit[],
  branches: Record<string, string>,
): string[] {
  const reachable = new Set<string>();
  const visit = (id: string | null | undefined) => {
    if (!id || reachable.has(id)) return;
    reachable.add(id);
    const c = commits.find((x) => x.id === id);
    if (!c) return;
    visit(c.parent);
    visit(c.parent2);
  };
  Object.values(branches).forEach(visit);
  return commits.filter((c) => !reachable.has(c.id)).map((c) => c.id);
}

/** `git restore <file>` — throw away working-tree edits, keep file at committed version. */
export function restoreFile(
  repo: RepoState,
  filename: string,
): OpResult & { restored: boolean } {
  const f = repo.files[filename];
  if (!f) {
    return {
      repo,
      restored: false,
      log: [{ kind: "err", text: `error: pathspec '${filename}' did not match any file` }],
    };
  }
  if (f.status !== "modified") {
    return {
      repo,
      restored: false,
      log: [
        { kind: "cmd", text: `git restore ${filename}` },
        { kind: "out", text: `Nothing to restore — ${filename} matches HEAD.` },
      ],
    };
  }
  return {
    repo: {
      ...repo,
      files: {
        ...repo.files,
        [filename]: {
          ...f,
          content: f.committed,
          staged: null,
          status: "clean",
        },
      },
    },
    restored: true,
    log: [
      { kind: "cmd", text: `git restore ${filename}` },
      { kind: "ok", text: `Restored ${filename} to its committed version. Working-tree changes discarded.` },
    ],
  };
}

/**
 * `git reset --<mode> <target>` — move the current branch ref backwards.
 *
 * - soft: branch moves, staging area + working tree unchanged.
 * - mixed (default): branch moves, staging clears, working tree unchanged.
 * - hard: branch moves, staging clears, working tree resets to new tip.
 *
 * Commits passed over remain in `commits[]` so the reflog can resurrect them;
 * they're listed in `orphanedCommits` for visual dimming.
 */
export function resetBranch(
  repo: RepoState,
  mode: "soft" | "mixed" | "hard",
  targetCommitId: string,
): OpResult & { reset: boolean } {
  const branch = repo.head;
  const target = repo.commits.find((c) => c.id === targetCommitId);
  if (!target) {
    return {
      repo,
      reset: false,
      log: [{ kind: "err", text: `fatal: ambiguous argument '${targetCommitId.slice(0, 7)}'` }],
    };
  }

  const nextBranches = { ...repo.branches, [branch]: targetCommitId };
  const orphans = recomputeOrphans(repo.commits, nextBranches);

  // Build the new file state based on mode.
  // For lessons we model file content at each commit as that commit's "committed" snapshot.
  // We have only one tip's content; treat resetting as: take target's committed content from the
  // commit if we tracked it, otherwise leave files alone (soft/mixed semantics still hold).
  // Practically the lesson seeds per-commit content via the initial state when needed.
  const nextFiles = { ...repo.files };
  if (mode === "mixed" || mode === "hard") {
    // Unstage everything.
    Object.entries(repo.files).forEach(([name, f]) => {
      if (f.status === "staged") {
        nextFiles[name] = { ...f, staged: null, status: f.content === f.committed ? "clean" : "modified" };
      }
    });
  }
  if (mode === "hard") {
    // For hard reset, working tree matches the target commit's snapshot. The target commit's
    // file snapshot is encoded in `target.snapshot` if the lesson provides it; otherwise we
    // fall back to current `committed` (preserves visual continuity for simple lessons).
    const snapshot = target.snapshot;
    Object.entries(nextFiles).forEach(([name, f]) => {
      const desired = snapshot?.[name] ?? f.committed;
      nextFiles[name] = {
        ...f,
        content: desired,
        committed: desired,
        staged: null,
        status: "clean",
      };
    });
  } else {
    // Soft/mixed: the new tip's `committed` baseline should still reflect target's snapshot
    // (so future edits diff against it) but working content is left alone.
    const snapshot = target.snapshot;
    if (snapshot) {
      Object.entries(nextFiles).forEach(([name, f]) => {
        const newCommitted = snapshot[name] ?? f.committed;
        const stillModified = f.content !== newCommitted;
        nextFiles[name] = {
          ...f,
          committed: newCommitted,
          // soft preserves staged; mixed already cleared it above.
          status: mode === "soft" && f.status === "staged"
            ? "staged"
            : stillModified
              ? "modified"
              : "clean",
        };
      });
    }
  }

  const reflog = pushReflog(repo, {
    ref: "HEAD",
    commitId: targetCommitId,
    action: `reset: moving to ${targetCommitId.slice(0, 7)}`,
  });

  const modeFlag = mode === "mixed" ? "" : ` --${mode}`;
  return {
    repo: {
      ...repo,
      branches: nextBranches,
      files: nextFiles,
      reflog,
      orphanedCommits: orphans,
    },
    reset: true,
    log: [
      { kind: "cmd", text: `git reset${modeFlag} ${targetCommitId.slice(0, 7)}` },
      {
        kind: "ok",
        text:
          mode === "hard"
            ? `HEAD is now at ${targetCommitId.slice(0, 7)} ${target.msg}`
            : mode === "soft"
              ? `Moved ${branch} to ${targetCommitId.slice(0, 7)}. Working tree + index unchanged.`
              : `Moved ${branch} to ${targetCommitId.slice(0, 7)}. Index reset; working tree kept.`,
      },
    ],
  };
}

/**
 * `git revert <commit>` — create a NEW commit that undoes the changes from `commit`.
 * History is preserved. For lessons, the inverse file content comes from the commit's parent
 * snapshot (or the lesson's pre-baked `inverseSnapshot` on the commit).
 */
export function revertCommit(
  repo: RepoState,
  targetCommitId: string,
): OpResult & { reverted: boolean } {
  const target = repo.commits.find((c) => c.id === targetCommitId);
  if (!target) {
    return {
      repo,
      reverted: false,
      log: [{ kind: "err", text: `fatal: bad revision '${targetCommitId.slice(0, 7)}'` }],
    };
  }
  const parent = target.parent
    ? repo.commits.find((c) => c.id === target.parent)
    : null;
  const parentSnapshot = parent?.snapshot;

  const branch = repo.head;
  const branchTipId = repo.branches[branch];
  const branchTip = repo.commits.find((c) => c.id === branchTipId);
  const newId = randomSha();
  const msg = `Revert "${target.msg}"`;
  const newCommit: Commit = {
    id: newId,
    msg,
    branch,
    parent: branchTipId,
    x: (branchTip?.x ?? 0) + 1,
  };

  const nextFiles = { ...repo.files };
  if (parentSnapshot) {
    Object.entries(nextFiles).forEach(([name, f]) => {
      const desired = parentSnapshot[name] ?? f.committed;
      nextFiles[name] = {
        ...f,
        content: desired,
        committed: desired,
        staged: null,
        status: "clean",
      };
    });
    newCommit.snapshot = parentSnapshot;
  }

  const nextBranches = { ...repo.branches, [branch]: newId };
  const reflog = pushReflog(repo, {
    ref: "HEAD",
    commitId: newId,
    action: `revert: ${msg}`,
  });

  return {
    repo: {
      ...repo,
      commits: [...repo.commits, newCommit],
      branches: nextBranches,
      files: nextFiles,
      reflog,
      orphanedCommits: recomputeOrphans([...repo.commits, newCommit], nextBranches),
    },
    reverted: true,
    log: [
      { kind: "cmd", text: `git revert ${targetCommitId.slice(0, 7)}` },
      { kind: "ok", text: `[${branch} ${newId.slice(0, 7)}] ${msg}` },
    ],
  };
}

/**
 * Recover a previous HEAD state via reflog. `reflogIndex` indexes into reflog[] from the END
 * (HEAD@{0} = most recent = reflog[reflog.length - 1]).
 */
export function applyReflog(
  repo: RepoState,
  reflogIndex: number,
): OpResult & { recovered: boolean } {
  const reflog = repo.reflog ?? [];
  if (reflog.length === 0) {
    return {
      repo,
      recovered: false,
      log: [{ kind: "err", text: "No reflog entries to recover from." }],
    };
  }
  const realIdx = reflog.length - 1 - reflogIndex;
  const entry = reflog[realIdx];
  if (!entry) {
    return {
      repo,
      recovered: false,
      log: [{ kind: "err", text: `fatal: HEAD@{${reflogIndex}} not found` }],
    };
  }
  const target = repo.commits.find((c) => c.id === entry.commitId);
  if (!target) {
    return {
      repo,
      recovered: false,
      log: [{ kind: "err", text: `fatal: commit ${entry.commitId.slice(0, 7)} no longer exists` }],
    };
  }
  // Hard reset to that commit. Adds another reflog entry, recomputes orphans.
  const res = resetBranch(repo, "hard", entry.commitId);
  if (!res.reset) return { ...res, recovered: false };
  return {
    repo: res.repo,
    recovered: true,
    log: [
      { kind: "cmd", text: `git reset --hard HEAD@{${reflogIndex}}` },
      { kind: "ok", text: `Recovered to ${entry.commitId.slice(0, 7)} — "${target.msg}"` },
    ],
  };
}
